/**
 * @file strings.js
 *
 * Spanish UI Strings
 *
 * All user-facing text lives here — never hardcode Spanish strings in components.
 * This makes it easy to: find all text, stay consistent, and add translations later.
 * Status labels and colors come from shared/appointment-status.js (single source of truth).
 *
 * Pattern: ENTITY_STRINGS.KEY → "Texto en español"
 * Usage:   import { APPOINTMENT_STRINGS } from "../constants/strings";
 *          <Typography>{APPOINTMENT_STRINGS.LIST_TITLE}</Typography>
 *
 * Some objects contain maps (SPECIALTY_LABELS, STATUS_LABELS, etc.) which convert
 * database enum values to human-readable Spanish labels.
 */
import { STATUS_LABELS, STATUS_MUI_COLORS } from "../../../shared/appointment-status.js";

/** @constant {Object} AUTH_STRINGS - Login, registration, password reset, and OAuth UI text. */
export const AUTH_STRINGS = {
  // Login
  LOGIN_TITLE: "Iniciar Sesión",
  LOGIN_SUBTITLE: "Bienvenido a Consultto",
  LOGIN_EMAIL: "Correo electrónico",
  LOGIN_PASSWORD: "Contraseña",
  LOGIN_SUBMIT: "Iniciar Sesión",
  LOGIN_PATIENT_BUTTON: "Soy Paciente",
  LOGIN_PATIENT_DESC: "Accede con tu correo electrónico sin contraseña",
  LOGIN_FORGOT: "¿Olvidaste tu contraseña?",
  LOGIN_NO_ACCOUNT: "¿No tienes cuenta?",
  LOGIN_REGISTER: "Regístrate",
  LOGIN_OR: "o",

  // Patient register
  PATIENT_REGISTER_TITLE: "Crear Cuenta",
  PATIENT_REGISTER_SUBTITLE: "Registro de Paciente",
  PATIENT_FIRST_NAME: "Nombre",
  PATIENT_LAST_NAME: "Apellido",
  PATIENT_PHONE: "Teléfono (WhatsApp)",
  PATIENT_EMAIL: "Correo electrónico",
  PATIENT_SUBMIT: "Crear Cuenta",
  PATIENT_HAS_ACCOUNT: "¿Ya tienes cuenta?",
  PATIENT_LOGIN: "Inicia sesión",

  // Verify code
  VERIFY_TITLE: "Verificar Código",
  VERIFY_SUBTITLE: "Enviamos un código de 6 dígitos a",
  VERIFY_CODE_LABEL: "Código de verificación",
  VERIFY_SUBMIT: "Verificar",
  VERIFY_RESEND: "Reenviar código",
  VERIFY_RESEND_SUCCESS: "Código reenviado",

  // Professional register
  PRO_REGISTER_TITLE: "Registro Profesional",
  PRO_REGISTER_SUBTITLE: "Crea tu cuenta como profesional de salud",
  PRO_FIRST_NAME: "Nombre",
  PRO_LAST_NAME: "Apellido",
  PRO_EMAIL: "Correo electrónico",
  PRO_PHONE: "Teléfono (opcional)",
  PRO_PASSWORD: "Contraseña",
  PRO_CONFIRM_PASSWORD: "Confirmar contraseña",
  PRO_SUBMIT: "Registrarse",
  PRO_HAS_ACCOUNT: "¿Ya tienes cuenta?",
  PRO_LOGIN: "Inicia sesión",

  // Forgot password
  FORGOT_TITLE: "Recuperar Contraseña",
  FORGOT_SUBTITLE: "Ingresa tu correo y te enviaremos instrucciones",
  FORGOT_EMAIL: "Correo electrónico",
  FORGOT_SUBMIT: "Enviar instrucciones",
  FORGOT_BACK: "Volver al inicio de sesión",

  // Reset password
  RESET_TITLE: "Restablecer Contraseña",
  RESET_SUBTITLE: "Ingresa tu nueva contraseña",
  RESET_PASSWORD: "Nueva contraseña",
  RESET_CONFIRM: "Confirmar contraseña",
  RESET_SUBMIT: "Restablecer contraseña",
  RESET_SUCCESS: "Contraseña restablecida. Ya puedes iniciar sesión.",

  // Verify email (professional)
  VERIFY_EMAIL_TITLE: "Verificar Correo",
  VERIFY_EMAIL_SUBTITLE: "Hemos enviado un enlace de verificación a tu correo electrónico",
  VERIFY_EMAIL_SUCCESS: "Correo verificado correctamente. Ya puedes iniciar sesión.",

  // Common
  LOGOUT: "Cerrar sesión",
  LOADING: "Cargando...",

  // OAuth
  OAUTH_GOOGLE: "Continuar con Google",
  OAUTH_OR_LOGIN: "o inicia sesión con correo",
  OAUTH_OR_REGISTER: "o regístrate con",
  OAUTH_ERROR_GOOGLE: "Error al iniciar sesión con Google",
  OAUTH_APPLE: "Continuar con Apple",
  OAUTH_ERROR_APPLE: "Error al iniciar sesión con Apple",

  // Validation
  REQUIRED_FIELD: "Este campo es obligatorio",
  INVALID_EMAIL: "Correo electrónico inválido",
  PASSWORD_MIN: "Mínimo 10 caracteres",
  PASSWORDS_DONT_MATCH: "Las contraseñas no coinciden",
  CODE_LENGTH: "El código debe tener 6 dígitos",

  // Terms & consent
  ACCEPT_TERMS: "Acepto los",
  TERMS_LINK: "Términos y Condiciones",
  AND: "y el",
  PRIVACY_LINK: "Aviso de Privacidad",
  ACCEPT_TERMS_REQUIRED: "Debes aceptar los términos y condiciones",
};

/** @constant {Object} EMAIL_GATE_STRINGS - Email verification gate page for phone-registered patients. */
export const EMAIL_GATE_STRINGS = {
  TITLE: "Verifica tu correo electrónico",
  SUBTITLE: "Para acceder a tu panel de salud, necesitas agregar y verificar tu correo electrónico.",
  SUBTITLE_HAS_EMAIL: "Enviamos un código de verificación a tu correo. Ingrésalo para continuar.",
  EMAIL_LABEL: "Correo electrónico",
  EMAIL_PLACEHOLDER: "tu@correo.com",
  SEND_CODE: "Enviar código",
  CODE_LABEL: "Código de verificación",
  CODE_PLACEHOLDER: "123456",
  VERIFY: "Verificar",
  RESEND_CODE: "Reenviar código",
  CODE_SENT: "Te enviamos un código de verificación a",
  SUCCESS: "¡Correo verificado!",
  INVALID_EMAIL: "Ingresa un correo electrónico válido",
  INVALID_CODE: "El código debe tener 6 dígitos",
};

/** @constant {Object} APP_STRINGS - App name, navigation labels, and role display names. */
export const APP_STRINGS = {
  APP_NAME: "Consultto",
  NAV_HOME: "Inicio",
  NAV_CONTACTS: "Contactos",
  NAV_TASKS: "Tareas",
  NAV_PROJECTS: "Proyectos",
  NAV_PROFESSIONALS: "Profesionales",
  NAV_PATIENTS: "Pacientes",
  NAV_APPOINTMENTS: "Citas",
  NAV_MY_PROFILE: "Mi Perfil",
  NAV_SCHEDULE: "Horario",
  NAV_TODAY: "Hoy",
  NAV_CALENDAR: "Calendario",
  NAV_ADMIN: "Admin",

  // Roles
  ROLE_PACIENTE: "Paciente",
  ROLE_PROFESIONAL: "Profesional",
  ROLE_ADMIN: "Administrador",
};

/** @constant {string[]} MEXICO_STATES - 32 states of Mexico (for location forms when country = MX). */
export const MEXICO_STATES = [
  "Aguascalientes", "Baja California", "Baja California Sur", "Campeche",
  "Chiapas", "Chihuahua", "Ciudad de México", "Coahuila", "Colima",
  "Durango", "Guanajuato", "Guerrero", "Hidalgo", "Jalisco",
  "Estado de México", "Michoacán", "Morelos", "Nayarit", "Nuevo León",
  "Oaxaca", "Puebla", "Querétaro", "Quintana Roo", "San Luis Potosí",
  "Sinaloa", "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala",
  "Veracruz", "Yucatán", "Zacatecas",
];

/** @constant {Array<{code: string, label: string}>} SPANISH_COUNTRIES - Spanish-speaking countries for location dropdowns. */
export const SPANISH_COUNTRIES = [
  { code: "MX", label: "México" },
  { code: "ES", label: "España" },
  { code: "AR", label: "Argentina" },
  { code: "BO", label: "Bolivia" },
  { code: "CL", label: "Chile" },
  { code: "CO", label: "Colombia" },
  { code: "CR", label: "Costa Rica" },
  { code: "DO", label: "República Dominicana" },
  { code: "EC", label: "Ecuador" },
  { code: "SV", label: "El Salvador" },
  { code: "GT", label: "Guatemala" },
  { code: "HN", label: "Honduras" },
  { code: "PA", label: "Panamá" },
  { code: "PY", label: "Paraguay" },
  { code: "PE", label: "Perú" },
  { code: "PR", label: "Puerto Rico" },
  { code: "UY", label: "Uruguay" },
];

/** @constant {string[]} PROFESSIONAL_TITLES - Common professional title prefixes (Dr., Dra., Lic., etc.). */
export const PROFESSIONAL_TITLES = [
  "Dr.", "Dra.", "Lic.", "Licda.",
  "Psic.", "Nutr.", "Fis.", "Opt.", "Odont.", "C.D.",
];

/** @constant {Object} PROFESSIONAL_STRINGS - Professional profile, list, and specialty labels. */
export const PROFESSIONAL_STRINGS = {
  LIST_TITLE: "Profesionales",
  LIST_EMPTY: "No hay profesionales registrados",
  LIST_NO_RESULTS: "Ningún profesional coincide con tu búsqueda",
  SEARCH_PLACEHOLDER: "Buscar por nombre...",
  FILTER_SPECIALTY: "Especialidad",
  FILTER_ALL_SPECIALTIES: "Todas las especialidades",
  CLEAR_FILTERS: "Limpiar filtros",
  PROFILE_TITLE: "Perfil Profesional",
  EDIT_TITLE: "Editar Mi Perfil",
  BIO: "Biografía",
  BIO_PLACEHOLDER: "Escribe una breve descripción sobre ti y tu experiencia...",
  SPECIALTY: "Especialidad",
  SPECIALTY_OTHER: "Especificar especialidad",
  SESSION_DURATION: "Duración de sesión (minutos)",
  PRICE: "Precio por consulta",
  CURRENCY: "Moneda",
  BUFFER_TIME: "Tiempo entre citas (minutos)",
  SAVE: "Guardar cambios",
  SAVED: "Perfil actualizado correctamente",
  PATIENTS_TITLE: "Mis Pacientes",
  NO_PATIENTS: "Aún no tienes pacientes",
  VIEW_PROFILE: "Ver perfil",
  BOOK_APPOINTMENT: "Agendar cita",

  // Specialty labels — must match Prisma Specialty enum keys
  SPECIALTY_LABELS: {
    // Medicina General y Familiar
    MEDICINA_GENERAL: "Medicina General",
    MEDICINA_FAMILIAR: "Medicina Familiar",
    MEDICINA_INTERNA: "Medicina Interna",
    // Especialidades Quirúrgicas
    CIRUGIA_GENERAL: "Cirugía General",
    CIRUGIA_PLASTICA: "Cirugía Plástica",
    CIRUGIA_CARDIOVASCULAR: "Cirugía Cardiovascular",
    CIRUGIA_PEDIATRICA: "Cirugía Pediátrica",
    CIRUGIA_BARIATRICA: "Cirugía Bariátrica",
    CIRUGIA_MAXILOFACIAL: "Cirugía Maxilofacial",
    NEUROCIRUGIA: "Neurocirugía",
    // Especialidades Médicas
    CARDIOLOGIA: "Cardiología",
    DERMATOLOGIA: "Dermatología",
    ENDOCRINOLOGIA: "Endocrinología",
    GASTROENTEROLOGIA: "Gastroenterología",
    GERIATRIA: "Geriatría",
    HEMATOLOGIA: "Hematología",
    INFECTOLOGIA: "Infectología",
    NEFROLOGIA: "Nefrología",
    NEUMOLOGIA: "Neumología",
    NEUROLOGIA: "Neurología",
    ONCOLOGIA: "Oncología",
    REUMATOLOGIA: "Reumatología",
    UROLOGIA: "Urología",
    // Pediatría y Subespecialidades
    PEDIATRIA: "Pediatría",
    NEONATOLOGIA: "Neonatología",
    ONCOLOGIA_PEDIATRICA: "Oncología Pediátrica",
    NEUROLOGIA_PEDIATRICA: "Neurología Pediátrica",
    GASTROENTEROLOGIA_PEDIATRICA: "Gastroenterología Pediátrica",
    CARDIOLOGIA_PEDIATRICA: "Cardiología Pediátrica",
    // Ginecología y Obstetricia
    GINECOLOGIA: "Ginecología",
    MEDICINA_MATERNO_FETAL: "Medicina Materno-Fetal",
    REPRODUCCION_HUMANA: "Reproducción Humana",
    // Traumatología y Ortopedia
    TRAUMATOLOGIA: "Traumatología",
    ORTOPEDIA: "Ortopedia",
    MEDICINA_DEL_DEPORTE: "Medicina del Deporte",
    // Sentidos
    OFTALMOLOGIA: "Oftalmología",
    OTORRINOLARINGOLOGIA: "Otorrinolaringología",
    AUDIOLOGIA: "Audiología",
    // Salud Mental
    PSIQUIATRIA: "Psiquiatría",
    PSICOLOGIA: "Psicología",
    NEUROPSICOLOGIA: "Neuropsicología",
    PSICOLOGIA_INFANTIL: "Psicología Infantil",
    // Odontología
    ODONTOLOGIA: "Odontología",
    ORTODONCIA: "Ortodoncia",
    ENDODONCIA: "Endodoncia",
    PERIODONCIA: "Periodoncia",
    ODONTOLOGIA_PEDIATRICA: "Odontología Pediátrica",
    PROTESIS_DENTAL: "Prótesis Dental",
    IMPLANTOLOGIA_DENTAL: "Implantología Dental",
    // Nutrición
    NUTRICION: "Nutrición",
    NUTRICION_DEPORTIVA: "Nutrición Deportiva",
    NUTRICION_PEDIATRICA: "Nutrición Pediátrica",
    // Rehabilitación y Terapias
    FISIOTERAPIA: "Fisioterapia",
    MEDICINA_FISICA_REHABILITACION: "Medicina Física y Rehabilitación",
    TERAPIA_DE_LENGUAJE: "Terapia de Lenguaje",
    TERAPIA_OCUPACIONAL: "Terapia Ocupacional",
    // Otras Especialidades Médicas
    ANESTESIOLOGIA: "Anestesiología",
    RADIOLOGIA: "Radiología",
    PATOLOGIA: "Patología",
    MEDICINA_DE_URGENCIAS: "Medicina de Urgencias",
    MEDICINA_DEL_TRABAJO: "Medicina del Trabajo",
    ALGOLOGIA: "Algología",
    GENETICA_MEDICA: "Genética Médica",
    COLOPROCTOLOGIA: "Coloproctología",
    MEDICINA_NUCLEAR: "Medicina Nuclear",
    MEDICINA_PALIATIVA: "Medicina Paliativa",
    // Otras Profesiones de Salud
    OPTOMETRIA: "Optometría",
    PODOLOGIA: "Podología",
    QUIROPRAXIA: "Quiropraxia",
    // Otra
    OTRA: "Otra",
  },

  // Grouped specialties for dropdown rendering with ListSubheader
  SPECIALTY_GROUPS: [
    { group: "Pediatría", items: [
      ["PEDIATRIA", "Pediatría"], ["NEONATOLOGIA", "Neonatología"], ["ONCOLOGIA_PEDIATRICA", "Oncología Pediátrica"],
      ["NEUROLOGIA_PEDIATRICA", "Neurología Pediátrica"], ["GASTROENTEROLOGIA_PEDIATRICA", "Gastroenterología Pediátrica"],
      ["CARDIOLOGIA_PEDIATRICA", "Cardiología Pediátrica"],
    ]},
    { group: "Medicina General y Familiar", items: [
      ["MEDICINA_GENERAL", "Medicina General"], ["MEDICINA_FAMILIAR", "Medicina Familiar"], ["MEDICINA_INTERNA", "Medicina Interna"],
    ]},
    { group: "Especialidades Quirúrgicas", items: [
      ["CIRUGIA_GENERAL", "Cirugía General"], ["CIRUGIA_PLASTICA", "Cirugía Plástica"], ["CIRUGIA_CARDIOVASCULAR", "Cirugía Cardiovascular"],
      ["CIRUGIA_PEDIATRICA", "Cirugía Pediátrica"], ["CIRUGIA_BARIATRICA", "Cirugía Bariátrica"], ["CIRUGIA_MAXILOFACIAL", "Cirugía Maxilofacial"],
      ["NEUROCIRUGIA", "Neurocirugía"],
    ]},
    { group: "Especialidades Médicas", items: [
      ["CARDIOLOGIA", "Cardiología"], ["DERMATOLOGIA", "Dermatología"], ["ENDOCRINOLOGIA", "Endocrinología"],
      ["GASTROENTEROLOGIA", "Gastroenterología"], ["GERIATRIA", "Geriatría"], ["HEMATOLOGIA", "Hematología"],
      ["INFECTOLOGIA", "Infectología"], ["NEFROLOGIA", "Nefrología"], ["NEUMOLOGIA", "Neumología"],
      ["NEUROLOGIA", "Neurología"], ["ONCOLOGIA", "Oncología"], ["REUMATOLOGIA", "Reumatología"], ["UROLOGIA", "Urología"],
    ]},
    { group: "Ginecología y Obstetricia", items: [
      ["GINECOLOGIA", "Ginecología"], ["MEDICINA_MATERNO_FETAL", "Medicina Materno-Fetal"], ["REPRODUCCION_HUMANA", "Reproducción Humana"],
    ]},
    { group: "Traumatología y Ortopedia", items: [
      ["TRAUMATOLOGIA", "Traumatología"], ["ORTOPEDIA", "Ortopedia"], ["MEDICINA_DEL_DEPORTE", "Medicina del Deporte"],
    ]},
    { group: "Sentidos", items: [
      ["OFTALMOLOGIA", "Oftalmología"], ["OTORRINOLARINGOLOGIA", "Otorrinolaringología"], ["AUDIOLOGIA", "Audiología"],
    ]},
    { group: "Salud Mental", items: [
      ["PSIQUIATRIA", "Psiquiatría"], ["PSICOLOGIA", "Psicología"], ["NEUROPSICOLOGIA", "Neuropsicología"],
      ["PSICOLOGIA_INFANTIL", "Psicología Infantil"],
    ]},
    { group: "Odontología", items: [
      ["ODONTOLOGIA", "Odontología"], ["ORTODONCIA", "Ortodoncia"], ["ENDODONCIA", "Endodoncia"],
      ["PERIODONCIA", "Periodoncia"], ["ODONTOLOGIA_PEDIATRICA", "Odontología Pediátrica"],
      ["PROTESIS_DENTAL", "Prótesis Dental"], ["IMPLANTOLOGIA_DENTAL", "Implantología Dental"],
    ]},
    { group: "Nutrición", items: [
      ["NUTRICION", "Nutrición"], ["NUTRICION_DEPORTIVA", "Nutrición Deportiva"], ["NUTRICION_PEDIATRICA", "Nutrición Pediátrica"],
    ]},
    { group: "Rehabilitación y Terapias", items: [
      ["FISIOTERAPIA", "Fisioterapia"], ["MEDICINA_FISICA_REHABILITACION", "Medicina Física y Rehabilitación"],
      ["TERAPIA_DE_LENGUAJE", "Terapia de Lenguaje"], ["TERAPIA_OCUPACIONAL", "Terapia Ocupacional"],
    ]},
    { group: "Otras Especialidades Médicas", items: [
      ["ANESTESIOLOGIA", "Anestesiología"], ["RADIOLOGIA", "Radiología"], ["PATOLOGIA", "Patología"],
      ["MEDICINA_DE_URGENCIAS", "Medicina de Urgencias"], ["MEDICINA_DEL_TRABAJO", "Medicina del Trabajo"],
      ["ALGOLOGIA", "Algología"], ["GENETICA_MEDICA", "Genética Médica"], ["COLOPROCTOLOGIA", "Coloproctología"],
      ["MEDICINA_NUCLEAR", "Medicina Nuclear"], ["MEDICINA_PALIATIVA", "Medicina Paliativa"],
    ]},
    { group: "Otras Profesiones de Salud", items: [
      ["OPTOMETRIA", "Optometría"],
    ]},
    { group: "Otra", items: [
      ["OTRA", "Otra"],
    ]},
  ],
};

/** @constant {Object} PATIENT_STRINGS - Patient list, profile, and creation UI text. */
export const PATIENT_STRINGS = {
  LIST_TITLE: "Pacientes",
  LIST_EMPTY: "No hay pacientes registrados",
  DETAIL_TITLE: "Detalle del Paciente",
  PROFILE_TITLE: "Mi Perfil",
  EDIT_TITLE: "Editar Mi Perfil",
  DATE_OF_BIRTH: "Fecha de nacimiento",
  SAVE: "Guardar cambios",
  SAVED: "Perfil actualizado correctamente",
  APPOINTMENT_HISTORY: "Historial de citas",
  NO_APPOINTMENTS: "Sin citas registradas",

  // Professional creates patient
  CREATE_TITLE: "Registrar Paciente",
  CREATE_EMAIL: "Correo electrónico del paciente",
  CREATE_FIRST_NAME: "Nombre",
  CREATE_LAST_NAME: "Apellido",
  CREATE_SUBMIT: "Registrar paciente",
  CREATED: "Paciente registrado correctamente",

  // Patient panel — layout tabs
  TAB_HOME: "Mi Salud",
  TAB_APPOINTMENTS: "Mis Citas",
  TAB_MY_PROFESSIONALS: "Mis Doctores",
  TAB_SEARCH: "Buscar",
  TAB_PROFILE: "Mi Perfil",

  // Patient home
  HOME_TITLE: "Mi Salud",
  NEXT_APPOINTMENT: "Próxima cita",
  NO_UPCOMING: "No tienes citas próximas",
  BOOK_CTA: "Agendar cita",
  UPCOMING_COUNT: "Citas próximas",
  COMPLETED_COUNT: "Citas completadas",
  VIEW_DETAIL: "Ver detalle",

  // Patient appointments
  TAB_UPCOMING: "Próximas",
  TAB_HISTORY: "Historial",
  CANCEL_APPOINTMENT: "Cancelar",
  NO_UPCOMING_APPOINTMENTS: "No tienes citas próximas",
  NO_HISTORY: "Aún no tienes historial de citas",
  BOOK_FIRST_CTA: "Buscar doctor",

  // My professionals
  MY_PROFESSIONALS_TITLE: "Mis Doctores",
  NO_PROFESSIONALS: "Aún no tienes doctores",
  NO_PROFESSIONALS_CTA: "Busca uno para agendar tu primera cita",
  LAST_VISIT: "Última visita",
  TOTAL_VISITS: "visitas",
  BOOK_AGAIN: "Agendar de nuevo",
  VIEW_PROFILE: "Ver perfil",

  // Profile — health fields
  PERSONAL_INFO: "Información personal",
  HEALTH_INFO: "Información de salud",
  EMERGENCY_INFO: "Contacto de emergencia",
  BLOOD_TYPE: "Tipo de sangre",
  ALLERGIES: "Alergias y condiciones",
  ALLERGIES_PLACEHOLDER: "Ej: Alergia a penicilina, diabetes tipo 2...",
  EMERGENCY_CONTACT_NAME: "Nombre del contacto",
  EMERGENCY_CONTACT_PHONE: "Teléfono del contacto",

  // Health data consent (LFPDPPP Art. 8)
  HEALTH_CONSENT_TITLE: "Consentimiento para datos de salud",
  HEALTH_CONSENT_TEXT: "Tus datos de salud (tipo de sangre, alergias, contacto de emergencia) son datos personales sensibles protegidos por la LFPDPPP. Al guardar esta información, aceptas expresamente su tratamiento conforme a nuestro Aviso de Privacidad.",
  HEALTH_CONSENT_ACCEPT: "Acepto el tratamiento de mis datos de salud",
  HEALTH_CONSENT_CANCEL: "Cancelar",
  HEALTH_CONSENT_CONFIRM: "Guardar datos",
};

/** @constant {Object} APPOINTMENT_STRINGS - Appointment CRUD, status labels/colors, and action button text. */
export const APPOINTMENT_STRINGS = {
  LIST_TITLE: "Citas",
  LIST_EMPTY: "No hay citas registradas",
  LIST_NO_RESULTS: "Ninguna cita coincide con los filtros aplicados",
  FILTER_DATE_FROM: "Desde",
  FILTER_DATE_TO: "Hasta",
  FILTER_ALL_STATUSES: "Todos los estados",
  FILTER_APPLY: "Buscar",
  FILTER_CLEAR: "Limpiar filtros",
  NEW_TITLE: "Nueva Cita",
  DETAIL_TITLE: "Detalle de Cita",
  PROFESSIONAL: "Profesional",
  PATIENT: "Paciente",
  PATIENT_SEARCH_PLACEHOLDER: "Buscar paciente por nombre, teléfono o correo...",
  PATIENT_NO_OPTIONS: "Sin resultados",
  DATE_TIME: "Fecha y hora",
  END_TIME: "Hora de finalización",
  REASON: "Motivo de consulta",
  REASON_PLACEHOLDER: "Describe brevemente el motivo de tu consulta...",
  NOTES: "Notas",
  NOTES_PLACEHOLDER: "Escribe notas sobre la consulta...",
  STATUS: "Estado",
  SELECT_DATE_FIRST: "Selecciona un profesional y una fecha para ver horarios disponibles",
  CREATE: "Agendar cita",
  CREATED: "Cita creada correctamente",
  CANCEL_REASON: "Motivo de cancelación",
  CANCEL_REASON_REQUIRED: "El motivo de cancelación es obligatorio",
  CANCEL_REASON_MIN: "El motivo debe tener al menos 10 caracteres",

  // Actions
  CONFIRM: "Confirmar",
  COMPLETE: "Completar",
  CANCEL: "Cancelar",
  RESCHEDULE: "Reagendar",
  NO_SHOW: "No asistió",
  SAVE_NOTES: "Guardar notas",

  // Status labels and colors — from shared/appointment-status.js
  STATUS_LABELS,
  STATUS_COLORS: STATUS_MUI_COLORS,

  // Messages
  CONFIRMED: "Cita confirmada",
  COMPLETED: "Cita completada",
  CANCELLED: "Cita cancelada",
  RESCHEDULED: "Cita reagendada",
  NO_SHOW_MARKED: "Cita marcada como no asistida",
  NOTES_SAVED: "Notas guardadas",
};

/** @constant {Object} SCHEDULE_STRINGS - Weekly schedule management text. DAYS[i] matches dayOfWeek (0=Domingo). */
export const SCHEDULE_STRINGS = {
  TITLE: "Mi Horario",
  ADD_SLOT: "Agregar horario",
  DAY_OF_WEEK: "Día",
  START_TIME: "Hora de inicio",
  END_TIME: "Hora de fin",
  SAVE: "Guardar",
  DELETE: "Eliminar",
  CREATED: "Horario creado",
  UPDATED: "Horario actualizado",
  DELETED: "Horario eliminado",
  EMPTY: "No tienes horarios configurados",
  DAYS: ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"],

  // Tab labels for ScheduleManager
  TAB_WEEKLY: "Horario Semanal",
  TAB_OVERRIDES: "Excepciones",

  // Available slots
  NO_SLOTS: "No hay horarios disponibles para esta fecha",
  SELECT_DATE: "Selecciona una fecha",
  SELECT_SLOT: "Selecciona un horario",
};

/** @constant {Object} SCHEDULE_OVERRIDE_STRINGS - Per-date schedule exception UI text. */
export const SCHEDULE_OVERRIDE_STRINGS = {
  TITLE: "Excepciones de Horario",
  ADD: "Agregar excepción",
  DATE: "Fecha",
  TYPE: "Tipo",
  BLOCK_DAY: "Bloquear día completo",
  SPECIAL_HOURS: "Horario especial",
  REASON: "Motivo (opcional)",
  REASON_PLACEHOLDER: "Ej: Vacaciones, Congreso, etc.",
  EMPTY: "No tienes excepciones de horario configuradas",
  CREATED: "Excepción creada",
  UPDATED: "Excepción actualizada",
  DELETED: "Excepción eliminada",
  BLOCKED: "Bloqueado",
  SAVE: "Guardar",
};

/** @constant {Object} PUBLIC_STRINGS - Public booking flow and professional profile page text. */
export const PUBLIC_STRINGS = {
  BOOK_BUTTON: "Agendar cita",
  BOOKING_TITLE: "Confirmar cita",
  SELECT_DATE: "Selecciona una fecha",
  SELECT_SLOT: "Selecciona un horario",
  NO_SLOTS: "No hay horarios disponibles para esta fecha",
  REASON_LABEL: "Motivo de consulta (opcional)",
  REASON_PLACEHOLDER: "Describe brevemente el motivo de tu consulta...",
  CONFIRM_BOOKING: "Confirmar cita",
  BOOKING_SUCCESS: "¡Cita agendada! Te esperamos en el consultorio.",
  SESSION: "Duración",
  PRICE: "Precio",
  MINUTES: "min",
  NOT_FOUND: "Perfil no encontrado",
  NOT_FOUND_DESC: "El perfil que buscas no existe o fue eliminado.",
  ADD_EMAIL_TITLE: "¿Quieres recibir notificaciones por correo?",
  ADD_EMAIL_SKIP: "Ahora no",
  ADD_EMAIL_SUBMIT: "Guardar correo",
  ADD_EMAIL_PLACEHOLDER: "tu@correo.com",
  SCHEDULE_TITLE: "Horario de atención",
  VIEW_PHONE: "Ver teléfono",
  VIEW_EMAIL: "Ver correo",
  CONTACT_GATE_TITLE: "Datos de contacto",
  CONTACT_GATE_DESC: "Inicia sesión o crea una cuenta para ver el teléfono y correo de este profesional.",
  CONTACT_GATE_LOGIN: "Iniciar sesión",
  CONTACT_GATE_REGISTER: "Crear cuenta",
  SELECT_SERVICE: "Selecciona un servicio",
  SERVICE_FREE: "Consultar precio",
};

/** @constant {Object} DASHBOARD_STRINGS - Dashboard titles, stat labels, and quick action buttons. */
export const DASHBOARD_STRINGS = {
  TITLE: "Panel Principal",
  WELCOME: "Bienvenido",
  TODAY_APPOINTMENTS: "Citas de hoy",
  PENDING_APPOINTMENTS: "Citas pendientes",
  TOTAL_PATIENTS: "Total pacientes",
  MONTHLY_COMPLETED: "Completadas este mes",
  QUICK_ACTIONS: "Acciones rápidas",
  NEW_APPOINTMENT: "Nueva cita",
  VIEW_APPOINTMENTS: "Ver citas",
  VIEW_PATIENTS: "Ver pacientes",
  VIEW_SCHEDULE: "Ver horario",
  VIEW_PROFESSIONALS: "Ver profesionales",
  EDIT_PROFILE: "Editar perfil",
  // Admin quick actions
  ADMIN_USERS: "Gestionar usuarios",
  TODAY_VIEW: "Agenda de hoy",
  CALENDAR_VIEW: "Calendario",
  // Greeting
  GREETING_MORNING: "Buenos días",
  GREETING_AFTERNOON: "Buenas tardes",
  GREETING_EVENING: "Buenas noches",
  // Next appointment
  NEXT_APPOINTMENT: "Próxima cita",
  NO_APPOINTMENTS_TODAY: "No tienes citas programadas hoy",
  SHARE_PROFILE_CTA: "Comparte tu perfil para recibir citas",
  COPY_LINK: "Copiar enlace",
  LINK_COPIED: "Enlace copiado",
  // Today agenda strip
  TODAY_AGENDA: "Agenda de hoy",
  TODAY_CONFIRMED: "confirmadas",
  TODAY_PENDING: "pendientes",
  TODAY_COMPLETED: "completadas",
  // Plan usage
  PLAN_USAGE: "citas este mes",
  PLAN_UNLIMITED: "Ilimitadas",
  UPGRADE_CTA: "Sube a Pro",
  YOUR_PLAN: "Tu plan",
  // Performance metrics
  METRICS_TITLE: "Rendimiento del mes",
  CONFIRMATION_RATE: "Tasa de confirmación",
  CANCELLATIONS: "Cancelaciones",
  NO_SHOWS: "No asistió",
};

/** @constant {Object} COMMON_STRINGS - Shared UI strings used across multiple pages. */
export const COMMON_STRINGS = {
  BACK: "Volver",
  NOT_FOUND_APPOINTMENT: "Cita no encontrada",
  NOT_FOUND_PATIENT: "Paciente no encontrado",
  NOT_FOUND_PROFESSIONAL: "Profesional no encontrado",
  // Result count — use with template literal: `${count} ${RESULT_COUNT}`
  RESULT: "resultado",
  RESULTS: "resultados",
  // Empty state CTAs
  NEW_APPOINTMENT_CTA: "Agendar primera cita",
  NEW_PATIENT_CTA: "Registrar primer paciente",
};

/** @constant {Object} ADMIN_STRINGS - Admin panel labels. */
export const ADMIN_STRINGS = {
  TITLE: "Panel de Administración",

  // Sidebar navigation
  NAV_DASHBOARD: "Dashboard",
  NAV_USERS: "Usuarios",
  NAV_APPOINTMENTS: "Citas",
  NAV_PROFESSIONALS: "Profesionales",
  NAV_AUDIT: "Auditoría",
  NAV_NOTIFICATIONS: "Notificaciones",
  NAV_SETTINGS: "Configuración",

  // User management
  USERS_TITLE: "Gestión de Usuarios",
  SEARCH_PLACEHOLDER: "Buscar por nombre, correo o teléfono...",
  FILTER_ROLE: "Rol",
  ALL_ROLES: "Todos los roles",
  ROLE_LABELS: { PACIENTE: "Paciente", PROFESIONAL: "Profesional", ADMIN: "Admin" },
  ACTIVE: "Activo",
  INACTIVE: "Inactivo",
  ACTIVATE: "Activar",
  DEACTIVATE: "Desactivar",
  CHANGE_ROLE: "Cambiar rol",
  EMPTY: "No se encontraron usuarios",
  VIEW_USER: "Ver",
  DELETE_USER: "Eliminar",
  DELETE_CONFIRM_TITLE: "Eliminar usuario",
  DELETE_CONFIRM_MSG: "¿Estás seguro de que quieres eliminar este usuario? Esta acción no se puede deshacer.",
  IMPERSONATE: "Iniciar sesión como",
  IMPERSONATE_BANNER: "Estás viendo como",
  IMPERSONATE_RETURN: "Volver al admin",

  // Stats
  STATS_TITLE: "Estadísticas de la plataforma",
  TOTAL_PATIENTS: "Pacientes",
  TOTAL_PROFESSIONALS: "Profesionales",
  TOTAL_ADMINS: "Administradores",
  TOTAL_APPOINTMENTS: "Citas totales",
  PENDING: "Pendientes",
  COMPLETED: "Completadas",
  CANCELLED: "Canceladas",

  // Dashboard
  DASHBOARD_TITLE: "Dashboard",
  APPOINTMENTS_TODAY: "Citas hoy",
  USERS_THIS_WEEK: "Usuarios esta semana",
  GROWTH: "Crecimiento",
  SIGNUPS_CHART: "Registros (últimos 30 días)",
  APPOINTMENTS_CHART: "Citas creadas (últimos 30 días)",
  RECENT_SIGNUPS: "Registros recientes",
  RECENT_ACTIVITY: "Actividad reciente",
  QUICK_LINKS: "Accesos rápidos",
  NO_TREND_DATA: "Sin datos aún",

  // Audit log
  AUDIT_TITLE: "Registro de Auditoría",
  AUDIT_EMPTY: "No se encontraron registros de auditoría",
  AUDIT_ACTIONS: {
    USER_ROLE_CHANGED: "Cambio de rol",
    USER_TOGGLED_ACTIVE: "Activar/Desactivar usuario",
    USER_DELETED: "Usuario eliminado",
    USER_IMPERSONATED: "Sesión como usuario",
    SETTING_UPDATED: "Configuración actualizada",
  },
  AUDIT_FILTER_ACTION: "Acción",
  AUDIT_ALL_ACTIONS: "Todas las acciones",

  // Notification log
  NOTIF_TITLE: "Registro de Notificaciones",
  NOTIF_EMPTY: "No se encontraron notificaciones",
  NOTIF_TYPE_WHATSAPP: "WhatsApp",
  NOTIF_TYPE_EMAIL: "Email",
  NOTIF_TYPE_PUSH: "Push",
  NOTIF_STATUS_SENT: "Enviado",
  NOTIF_STATUS_FAILED: "Fallido",
  NOTIF_STATUS_PENDING: "Pendiente",
  NOTIF_RETRY: "Reintentar",
  NOTIF_FILTER_TYPE: "Tipo",
  NOTIF_FILTER_STATUS: "Estado",
  NOTIF_ALL_TYPES: "Todos",
  NOTIF_ALL_STATUSES: "Todos",

  // Settings
  SETTINGS_TITLE: "Configuración de la Plataforma",
  SETTINGS_GENERAL: "General",
  SETTINGS_NOTIFICATIONS: "Notificaciones",
  SETTINGS_SAVE: "Guardar",
  SETTINGS_SAVED: "Configuración guardada",

  // Professional management
  PROFESSIONALS_TITLE: "Gestión de Profesionales",
  PROFESSIONALS_EMPTY: "No se encontraron profesionales",

  // Appointment management
  APPOINTMENTS_TITLE: "Todas las Citas",
  APPOINTMENTS_EMPTY: "No se encontraron citas",

  // Referral management
  NAV_REFERRALS: "Referidos",
  REFERRALS_TITLE: "Programa de Referidos",
  REFERRALS_EMPTY: "No hay referidos registrados",
  REFERRALS_TOTAL: "Total",
  REFERRALS_PENDING: "Pendientes",
  REFERRALS_COMPLETED: "Completados",
  REFERRALS_EXPIRED: "Expirados",
  REFERRALS_REFERRER: "Referidor",
  REFERRALS_REFERRED: "Referido",
  REFERRALS_STATUS: "Estado",
  REFERRALS_DATE: "Fecha de registro",
  REFERRALS_REWARD_DATE: "Fecha de recompensa",
};

/** @constant {Object} SUBSCRIPTION_STRINGS - Re-exported from shared billing strings. */
import { BILLING as _BILLING } from "../../../shared/billing-strings";
export const SUBSCRIPTION_STRINGS = {
  ..._BILLING,
  // Web-specific key aliases (backward compat with existing consumers)
  UPGRADE_CTA: _BILLING.UPGRADE_CTA_FULL,
  REFERRAL_STATS_TOTAL: _BILLING.REFERRAL_TOTAL,
  REFERRAL_STATS_COMPLETED: _BILLING.REFERRAL_COMPLETED,
  REFERRAL_STATS_PENDING: _BILLING.REFERRAL_PENDING,
  REFERRAL_STATS_MONTHS: _BILLING.REFERRAL_MONTHS,
  // Fiscal data form (CFDI invoicing). These keys were referenced by Billing.jsx
  // (label/hint/button/toast) but never defined, so the fiscal Inputs rendered
  // with no <label>, the save button had no text and the toasts were empty.
  FISCAL_RFC: "RFC",
  FISCAL_RFC_HELPER: "Tu Registro Federal de Contribuyentes (12 o 13 caracteres).",
  FISCAL_CP: "Código postal fiscal",
  FISCAL_CP_HELPER: "El código postal de tu domicilio fiscal.",
  FISCAL_RAZON_SOCIAL: "Razón social",
  FISCAL_RAZON_SOCIAL_HELPER: "Nombre o razón social tal como aparece en tu constancia.",
  FISCAL_REGIMEN: "Régimen fiscal",
  FISCAL_USO_CFDI: "Uso del CFDI",
  FISCAL_CONSTANCIA: "Constancia de Situación Fiscal",
  FISCAL_CONSTANCIA_HELPER: "Sube tu Constancia de Situación Fiscal en PDF.",
  FISCAL_CONSTANCIA_UPLOADED: "Constancia cargada.",
  FISCAL_SAVE: "Guardar datos fiscales",
  FISCAL_SAVED: "Datos fiscales guardados.",
  FISCAL_SAVE_ERROR: "No se pudieron guardar los datos fiscales.",
};

/** @constant {Object} ERROR_STRINGS - Error boundary and generic error page text. */
export const ERROR_STRINGS = {
  TITLE: "Algo salió mal",
  MESSAGE: "Ocurrió un error inesperado. Por favor, recarga la página o intenta de nuevo más tarde.",
  RELOAD: "Recargar página",
};

/** @constant {Object} LEGAL_STRINGS - Privacy policy and terms of service page text. */
/** @constant {Object} WAITLIST_STRINGS - Waitlist join, toggle, and notification UI text. */
export const WAITLIST_STRINGS = {
  JOIN_TITLE: "¿Quieres que te avisemos cuando haya un espacio?",
  JOIN_SUBTITLE: "Te enviaremos un correo cuando se abra disponibilidad.",
  JOIN_BUTTON: "Unirme a la lista",
  JOINED_TITLE: "Estás en la lista de espera",
  JOINED_SUBTITLE: "Te avisaremos si se abre un espacio.",
  LEAVE_BUTTON: "Salir de la lista",
  DURATION_LABEL: "¿Por cuánto tiempo?",
  DURATION_7: "7 días",
  DURATION_15: "15 días",
  DURATION_30: "30 días",
  DURATION_60: "60 días",
  NO_AVAILABILITY_CTA: "Avísarme cuando haya disponibilidad",
  SUBSCRIBE_BUTTON: "Suscribirme a la lista de espera",
  NO_AVAILABILITY_LONG: "No hay citas disponibles en los próximos 2 meses.",
  NEXT_AVAILABLE: "Próxima cita disponible:",
  PROMPT: "¿Quieres que te avisemos si se abre un espacio antes?",
  PROMPT_SUBTITLE: "Te notificaremos si hay una cancelación y se libera un horario más cercano.",
  AUTO_CANCEL_WARNING: "Al confirmar, tu cita del {date} será cancelada automáticamente.",
  TOGGLE_LABEL: "Activar lista de espera",
  TOGGLE_DESCRIPTION: "Cuando se abre un espacio, notificamos automáticamente a los pacientes en tu lista.",
  PATIENTS_IN_LIST: "pacientes en lista",
  NOTIFY_WAITLIST_QUESTION: "¿Quieres notificar a tu lista de espera sobre este espacio?",
  NOTIFY_YES: "Sí, notificar",
  NOTIFY_NO: "No",
  DASHBOARD_LABEL: "Lista de espera",
  JOINED_SUCCESS: "Te uniste a la lista de espera",
  LEFT_SUCCESS: "Saliste de la lista de espera",
  TOGGLE_ENABLED: "Lista de espera activada",
  TOGGLE_DISABLED: "Lista de espera desactivada",
};

export const LEGAL_STRINGS = {
  PRIVACY_TITLE: "Aviso de Privacidad",
  TERMS_TITLE: "Términos y Condiciones",
  LAST_UPDATED: "Última actualización",
  FOOTER_PRIVACY: "Aviso de Privacidad",
  FOOTER_TERMS: "Términos y Condiciones",
};

/** @constant {Object} SEO_STRINGS - SEO meta tag defaults and templates. */
export const SEO_STRINGS = {
  SITE_NAME: "Consultto",
  DEFAULT_TITLE: "Consultto | Gestión de Citas Médicas",
  DEFAULT_DESCRIPTION: "Plataforma de gestión de citas para profesionales de salud y pacientes en México.",
  PROFILE_TITLE_TEMPLATE: (name, specialty) => `${name}, ${specialty} | Consultto`,
  PROFILE_DESCRIPTION_TEMPLATE: (name, specialty) =>
    `Agenda tu cita con ${name}, ${specialty}. Consulta disponibilidad y reserva en línea en Consultto.`,
};
